import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Copy, Mail } from 'lucide-react';
import {
  buildSignatureHtml,
  type SignatureFields as Fields,
  type SignatureMode,
  OFFICE_PHONE,
  OFFICE_FAX,
  COMPANY_WEBSITE,
  COMPANY_TAGLINE,
} from '@atek/email-signature';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useSession } from '@/hooks/useSession';
import { useProfilePhoto } from '@/hooks/useProfilePhoto';
import { getUser } from '@/features/users/api';
import { api } from '@/lib/api';

/**
 * Format a North-American phone number as the user types.
 * Accepts any input, extracts digits, produces `+1 XXX XXX-XXXX`.
 * Partial values stay partial (no trailing separators).
 */
/**
 * Downscale a data-URL image to a small circular JPEG so it fits inside
 * Gmail's 10 000-char signature limit when inlined as base64.
 * 96×96 @ 0.8 quality typically comes out to ~3-5 KB of base64.
 */
async function shrinkPhotoForEmail(src: string, size = 96, quality = 0.8): Promise<string> {
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('photo load failed'));
    img.src = src;
  });
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d unavailable');
  // White background — looks neutral on light email themes, harmless otherwise.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, size, size);
  ctx.save();
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  ctx.clip();
  // Cover: fit the smaller side into the circle.
  const scale = size / Math.min(img.naturalWidth, img.naturalHeight);
  const w = img.naturalWidth * scale;
  const h = img.naturalHeight * scale;
  ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
  ctx.restore();
  return canvas.toDataURL('image/jpeg', quality);
}

function formatNAPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 11);
  if (digits.length === 0) return '';
  const body = digits.startsWith('1') ? digits.slice(1) : digits;
  const area = body.slice(0, 3);
  const mid = body.slice(3, 6);
  const last = body.slice(6, 10);
  let out = '+1';
  if (area) out += ` ${area}`;
  if (mid) out += ` ${mid}`;
  if (last) out += `-${last}`;
  return out;
}

const DEFAULT_FIELDS: Fields = {
  firstName: '',
  lastName: '',
  title: '',
  email: '',
  extension: '',
  mobile: '',
  linkedin: '',
};

const COBALT = '#5A1DEF';

async function copyHtml(html: string) {
  try {
    // Rich-text (paste into Gmail/Outlook keeps formatting)
    const blobHtml = new Blob([html], { type: 'text/html' });
    const blobText = new Blob([html.replace(/<[^>]+>/g, '')], { type: 'text/plain' });
    await navigator.clipboard.write([new ClipboardItem({ 'text/html': blobHtml, 'text/plain': blobText })]);
    toast.success('Signature copied (rich text)');
  } catch {
    await navigator.clipboard.writeText(html);
    toast.success('HTML copied');
  }
}

async function copySource(html: string) {
  await navigator.clipboard.writeText(html);
  toast.success('HTML source copied');
}

export function EmailSignatureTab() {
  const { user } = useSession();
  const [fields, setFields] = useState<Fields>(DEFAULT_FIELDS);
  const [mode, setMode] = useState<SignatureMode>('new');
  const [includePhoto, setIncludePhoto] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  // Fetch the full user record (session only has name/email — profile fields
  // like position, mobile, extension, linkedin live on the user document).
  const profileQuery = useQuery({
    queryKey: ['signature-profile', user?._id],
    queryFn: () => getUser(user!._id),
    enabled: !!user?._id,
    staleTime: 5 * 60 * 1000,
  });

  const [profilePhoto] = useProfilePhoto(user?._id);
  const [smallPhoto, setSmallPhoto] = useState<string | null>(null);

  // Shrink the profile photo so the rendered signature fits inside Gmail's
  // 10 000-char limit. Progressively tries 192→160→128→96 @ 0.85, picks the
  // largest version whose base64 data URL is under ~7 KB (leaves headroom
  // for HTML + markup).
  useEffect(() => {
    let cancelled = false;
    if (!profilePhoto) {
      setSmallPhoto(null);
      return;
    }
    (async () => {
      const budget = 7200; // chars
      for (const size of [192, 160, 128, 96]) {
        try {
          const dataUrl = await shrinkPhotoForEmail(profilePhoto, size, 0.85);
          if (dataUrl.length <= budget) {
            if (!cancelled) setSmallPhoto(dataUrl);
            return;
          }
        } catch {
          /* try next size */
        }
      }
      // Last resort: heavy compression
      try {
        const dataUrl = await shrinkPhotoForEmail(profilePhoto, 96, 0.7);
        if (!cancelled) setSmallPhoto(dataUrl);
      } catch {
        if (!cancelled) setSmallPhoto(profilePhoto);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profilePhoto]);

  // Prefill identity + contact fields from the profile once it loads.
  useEffect(() => {
    if (prefilled || !profileQuery.data) return;
    const p = profileQuery.data;
    setFields((prev) => ({
      ...prev,
      firstName: prev.firstName || p.firstname || '',
      lastName: prev.lastName || p.lastname || '',
      title: prev.title || p.position || '',
      email: prev.email || p.email || '',
      extension: prev.extension || p.extension || '',
      mobile: prev.mobile || formatNAPhone(p.mobile || ''),
      linkedin: prev.linkedin || p.linkedin || '',
    }));
    setPrefilled(true);
  }, [profileQuery.data, prefilled]);

  // Prefer a permanent public GCS URL (set when the user uploads a photo
  // via My Profile → Apply). Falls back to the inlined, shrunk base64 photo
  // for users who haven't re-saved yet.
  const hostedPhotoUrl = profileQuery.data?.profile_photo_url || null;
  const photoForSignature = includePhoto ? hostedPhotoUrl ?? smallPhoto ?? null : null;
  // The checkbox / avatar should be enabled whenever the user has a photo
  // available — either the hosted GCS URL (PG source of truth, works on any
  // browser) or the localStorage cache from a recent upload. Previously the
  // gate was localStorage-only, so the checkbox was permanently disabled on
  // any fresh browser even when the photo existed in PG.
  const availablePhoto = profilePhoto || hostedPhotoUrl;
  const html = useMemo(
    () => buildSignatureHtml(fields, mode, photoForSignature),
    [fields, mode, photoForSignature],
  );

  const [pushing, setPushing] = useState(false);
  const canPushToGmail = Boolean(user?.email?.toLowerCase().endsWith('@atek.io'));

  const pushToGmail = async (which: SignatureMode) => {
    const htmlToPush = buildSignatureHtml(fields, which, photoForSignature);
    setPushing(true);
    try {
      await api.post<{ ok: true }>('/v4/gmail-signature/apply', { html: htmlToPush });
      toast.success(`${which === 'new' ? 'New-email' : 'Reply'} signature applied to Gmail`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Gmail push failed';
      toast.error('Failed to push signature', { description: msg });
    } finally {
      setPushing(false);
    }
  };


  const set = <K extends keyof Fields>(key: K) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFields((prev) => ({ ...prev, [key]: e.target.value }));

  const setMobile = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFields((prev) => ({ ...prev, mobile: formatNAPhone(e.target.value) }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,420px)_1fr] gap-6 items-start">
      {/* Form */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Your details</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="firstName">First name</Label>
            <Input id="firstName" value={fields.firstName} onChange={set('firstName')} placeholder="Hakim" />
          </div>
          <div>
            <Label htmlFor="lastName">Last name</Label>
            <Input id="lastName" value={fields.lastName} onChange={set('lastName')} placeholder="Rouab" />
          </div>
        </div>
        <div>
          <Label htmlFor="title">Title</Label>
          <Input id="title" value={fields.title} onChange={set('title')} placeholder="CEO" />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={fields.email} disabled />
          <p className="text-[10px] text-slate-500 mt-0.5">From your ATEK profile</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Office phone</Label>
            <Input value={OFFICE_PHONE} disabled />
            <p className="text-[10px] text-slate-500 mt-0.5">Company main line (fixed)</p>
          </div>
          <div>
            <Label htmlFor="extension">Extension</Label>
            <Input
              id="extension"
              value={fields.extension}
              onChange={set('extension')}
              placeholder="5001"
              inputMode="numeric"
            />
            <p className="text-[10px] text-slate-500 mt-0.5">e.g. 5001 → rendered as x5001</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="mobile">Mobile</Label>
            <Input
              id="mobile"
              value={fields.mobile}
              onChange={setMobile}
              inputMode="tel"
              placeholder="+1 514 555-1234"
            />
          </div>
          <div>
            <Label>Fax</Label>
            <Input value={OFFICE_FAX} disabled />
            <p className="text-[10px] text-slate-500 mt-0.5">Company fax (fixed)</p>
          </div>
        </div>
        <div>
          <Label htmlFor="linkedin">LinkedIn URL</Label>
          <Input id="linkedin" value={fields.linkedin} onChange={set('linkedin')} placeholder="https://linkedin.com/in/..." />
        </div>

        <label
          htmlFor="include-photo"
          className={`flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer mt-2 ${
            includePhoto ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:border-slate-300'
          } ${!availablePhoto ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          <Checkbox
            id="include-photo"
            checked={includePhoto}
            disabled={!availablePhoto}
            onCheckedChange={(v) => setIncludePhoto(Boolean(v))}
          />
          <div className="flex-1">
            <div className="text-sm">Include profile photo</div>
            <div className="text-[10px] text-slate-500">
              {availablePhoto
                ? 'Uses your ATEK profile photo as a circular avatar.'
                : 'Upload a profile photo via My Profile to enable this.'}
            </div>
          </div>
          {availablePhoto && (
            <img src={availablePhoto} alt="" className="h-9 w-9 rounded-full object-cover border" />
          )}
        </label>

        <div className="pt-2 border-t mt-2 text-[11px] text-slate-500 space-y-0.5">
          <div><span className="text-slate-400">Website:</span> {COMPANY_WEBSITE} <span className="text-slate-400">(fixed)</span></div>
          <div><span className="text-slate-400">Tagline:</span> {COMPANY_TAGLINE} <span className="text-slate-400">(fixed)</span></div>
        </div>
      </div>

      {/* Preview + copy */}
      <div className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Preview</h3>
            <div className="inline-flex rounded-md border overflow-hidden text-xs">
              <button
                type="button"
                onClick={() => setMode('new')}
                className={`px-3 py-1.5 ${mode === 'new' ? 'bg-[#5A1DEF] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              >
                New email
              </button>
              <button
                type="button"
                onClick={() => setMode('reply')}
                className={`px-3 py-1.5 border-l ${mode === 'reply' ? 'bg-[#5A1DEF] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              >
                Reply / Forward
              </button>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => copySource(html)} className="gap-1.5">
              <Copy className="h-3.5 w-3.5" />
              Copy HTML
            </Button>
            <Button size="sm" variant="outline" onClick={() => copyHtml(html)} className="gap-1.5">
              <Copy className="h-3.5 w-3.5" />
              Copy as rich text
            </Button>
            <Button
              size="sm"
              onClick={() => pushToGmail(mode)}
              disabled={!canPushToGmail || pushing}
              className="gap-1.5"
              style={{ backgroundColor: COBALT }}
              title={canPushToGmail ? `Push current mode to ${user?.email}` : 'Only @atek.io Workspace accounts can push'}
            >
              <Mail className="h-3.5 w-3.5" />
              {pushing ? 'Pushing…' : `Push ${mode === 'new' ? 'New' : 'Reply'} to Gmail`}
            </Button>
          </div>
        </div>

        <div
          ref={previewRef}
          className="p-6 bg-white rounded-lg border min-h-[220px]"
          dangerouslySetInnerHTML={{ __html: html }}
        />

        <details className="border rounded-lg bg-slate-50">
          <summary className="px-3 py-2 text-xs font-medium text-slate-700 cursor-pointer">
            HTML source
          </summary>
          <pre className="p-3 text-[11px] text-slate-700 overflow-x-auto whitespace-pre-wrap">{html}</pre>
        </details>

        <div className="text-xs text-slate-500 space-y-1">
          <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            <strong>Gmail API limit:</strong> only <em>one</em> signature per address can be set via
            API — it&apos;s used for both new emails and replies. To have a different reply
            signature, create two signatures in Gmail UI and assign them there manually.
          </p>
          <p>
            <strong>Gmail UI alternative:</strong> Settings → See all settings → General → Signature
            → create two signatures ("Full" and "Reply"), then set <em>"For New Emails Use"</em> =
            Full and <em>"On Reply/Forward Use"</em> = Reply.
          </p>
          <p>
            <strong>Outlook:</strong> File → Options → Mail → Signatures → separate dropdowns for{' '}
            <em>New messages</em> and <em>Replies/forwards</em>.
          </p>
        </div>
      </div>
    </div>
  );
}
